import { useCallback, useEffect, useState } from 'react';
import { getGitHubActivity, getLinearIssues, LinearIssue, SourceResult } from '@/services/systemHealth';

const POLL_MS = 60_000;

export interface Source<T> {
    /** Null until the source has answered once. */
    data: T | null;
    /** No answer yet and no error: the first request is still in flight. */
    loading: boolean;
    /** Real data, but the latest poll failed (e.g. an expired token): what's shown is from an earlier poll. */
    stale: boolean;
    error: string | null;
}

/** Last good answer wins; nothing (and `loading`) until a source has answered once. */
export function useSource<T>(load: () => Promise<SourceResult<T>>, pollMs = POLL_MS): [Source<T>, () => void] {
    const [result, setResult] = useState<{ data: T | null; error: string | null; answered: boolean }>({
        data: null,
        error: null,
        answered: false,
    });
    const reload = useCallback(
        () => load().then((r) => setResult((prev) => ({ data: r.data ?? prev.data, error: r.error, answered: true }))),
        [load]
    );
    useEffect(() => {
        reload();
        const id = setInterval(reload, pollMs);
        return () => clearInterval(id);
    }, [reload, pollMs]);
    const { data, error, answered } = result;
    return [{ data, loading: !answered, stale: data != null && error != null, error }, reload];
}

/** GitHub PRs and Linear issues for the Activity tab, polled once a minute. */
export function useDevActivity() {
    const [github, reloadGitHub] = useSource(getGitHubActivity);
    const [linear, reloadLinear] = useSource(getLinearIssues);
    return { github, linear, reloadGitHub, reloadLinear };
}

/** In-flight states in board order, then Done. */
export function linearColumns(issues: LinearIssue[]): string[] {
    const started = [...new Map(issues.filter((i) => i.stateType === 'started').map((i) => [i.state, i])).values()]
        .sort((a, b) => a.statePosition - b.statePosition)
        .map((i) => i.state);
    return [...started, 'Done'];
}

export const isDone = (i: LinearIssue) => i.stateType === 'completed';
// ponytail: matched by name since Linear has no "ready to test" category; rename here if the state is renamed
export const isReadyToTest = (i: LinearIssue) => /ready.*test/i.test(i.state);
/** Past development: in review, testing or waiting for deploy. Matched by name, like isReadyToTest. */
export const isComing = (i: LinearIssue) => !isDone(i) && /review|test|deploy/i.test(i.state);
